import { DocumentType, SubmissionTaskType, TaskTypeToDocumentTypeMap } from '@common/types';
import { AddModalOpenModes, FileRoles } from './types';
import * as Styled from './styles';
import { StyledLink } from '@common/components/Link/styles';
import { DocumentProcessingFlow } from './components/DocumentContentDetails/types';
import { formattingGuidelinesUrl } from '@common/constants';

export const epilConversionCheckboxTexts = {
  title: 'Would you like your PIL to be converted into an ePIL?',
  subtitle: (
    <>
      {`e-PILs are specially formatted PILs, that are accessible to the visually impaired and can be interpreted by screen
  readers. e-PILs do not include any graphics, tables or images. For more info `}
      <StyledLink to={process.env.REACT_APP_HELP_PRODUCT_GROUP_URL!} target="_blank">
        click here &gt;
      </StyledLink>
    </>
  ),
};

export const alternativeTextFileCheckboxTexts = {
  title: 'Do any of the images, graphs or tables in this PIL need alternative text?',
  subtitle: (
    <>
      {`If the images, graphs or tables give essential information that is not covered in the text of the PIL, please provide alternative text that can be displayed instead.`}
    </>
  ),
};

export const pilAutoApproveCheckboxTexts = {
  title: 'Do you want your PIL to be sent back to you to approve?',
  subtitle: null,
};

export const accessibilityServiceCheckboxTexts = {
  title: 'Would you like your ePIL to be made available to the accessibility service?',
  subtitle: 'The accessibility feature enables HCPs and Patients to request large print and braille versions of an ePIL.',
};

export const FILE_EXTENSIONS = {
  [DocumentType.Pil.toString()]: ['pdf'],
  [DocumentType.Epil.toString()]: ['docx'],
  [DocumentType.Rmm.toString()]: ['pdf'],
  [DocumentType.UserManual.toString()]: ['pdf'],
  [DocumentType.Dhcp.toString()]: ['pdf'],
  [DocumentType.SafetyAlert.toString()]: ['pdf'],
  [DocumentType.ProductInformation.toString()]: ['pdf'],
  [DocumentType.Smpc.toString()]: ['docx'],
};

export const FILE_EXTENSIONS_BY_ROLE = {
  [FileRoles.Notes.toString()]: ['docx'],
  [FileRoles.TvtFirstTvpFile.toString()]: ['tvp12'],
  [FileRoles.TvtSecondTvpFile.toString()]: ['tvp12'],
  [FileRoles.TvtPdfFile.toString()]: ['pdf'],
};

export const OLD_WORD_FILES_EXTENSIONS = ['doc'];

export const getModalContent = (
  mode: AddModalOpenModes,
  documentProcessingFlow: DocumentProcessingFlow,
  type?: DocumentType,
  taskType?: SubmissionTaskType,
) => {
  // By default epils are treated as pils, the QRD flow is the only exception here for modal content
  const docType = documentProcessingFlow === DocumentProcessingFlow.qrdPil && taskType ? TaskTypeToDocumentTypeMap[taskType] : type;

  switch (docType) {
    case DocumentType.Smpc:
      return {
        title: `${mode} SmPC`,
        fileUploadTitle: 'Upload your SmPC',
        description: (
          <Styled.Description>
            <Styled.DescriptionText>
              Please make sure you have all the details ready, and you have checked that your SmPC:
            </Styled.DescriptionText>
            <Styled.DescriptionList>
              <Styled.DescriptionListItem>does not contain track changes</Styled.DescriptionListItem>
              <Styled.DescriptionListItem>is not password protected</Styled.DescriptionListItem>
              <Styled.DescriptionListItem>
                does not contain the Quality Review Document (QRD) sections as images
              </Styled.DescriptionListItem>
              <Styled.DescriptionListItem>does not contain nested tables</Styled.DescriptionListItem>
              <Styled.DescriptionListItem>does not contain any unsupported fonts</Styled.DescriptionListItem>
              <Styled.DescriptionListItem>is not in compatibility mode for versions of MS Word prior to 2013</Styled.DescriptionListItem>
            </Styled.DescriptionList>
            <Styled.DescriptionSubtext>
              Further information on document acceptance criteria can be found{' '}
              <StyledLink to={formattingGuidelinesUrl} target="_blank">
                here &gt;
              </StyledLink>
            </Styled.DescriptionSubtext>
          </Styled.Description>
        ),
        fileSubText: '.DOCX (Word 2013 or later) 3MB max',
      };

    case DocumentType.Pil:
      if (documentProcessingFlow === DocumentProcessingFlow.qrdPil && mode !== 'Replace') {
        // For pil create/update in qrd flow
        return {
          title: `${mode} PIL`,
          fileUploadTitle: 'Upload your PIL',
          secondaryUploadTitle: 'Would you like to include a pdf version of your PIL?',
          secondaryUploadDescription:
            'This should be a high quality PDF copy of your PIL that users can print and read it easily, please note that if you do not provide a seperate pdf version we will create one using the word document above',
          description: (
            <Styled.Description>
              <Styled.DescriptionText>
                Please make sure you have all the details ready, and you have checked that your PIL:
              </Styled.DescriptionText>
              <Styled.DescriptionList>
                <Styled.DescriptionListItem>is the QRD version submitted to the regulatory authority</Styled.DescriptionListItem>
                <Styled.DescriptionListItem>is not password protected</Styled.DescriptionListItem>
                <Styled.DescriptionListItem>is not a scanned document</Styled.DescriptionListItem>
                <Styled.DescriptionListItemBold>is not in compatibility mode for versions of MS Word prior to 2013</Styled.DescriptionListItemBold>
              </Styled.DescriptionList>
              <Styled.DescriptionSubtext>
                Further information on document acceptance criteria can be found{' '}
                <StyledLink to={process.env.REACT_APP_HELP_PRODUCT_GROUP_URL!} target="_blank">
                  here &gt;
                </StyledLink>
              </Styled.DescriptionSubtext>
            </Styled.Description>
          ),
          fileSubText: '.DOCX (Word 2013 or later) 5MB max',
          secondaryfileSubText: '.PDF 3MB max',
        };
      } else {
        // For pil create/update in pdf flow
        return {
          title: `${mode} PIL`,
          fileUploadTitle: 'Upload your PIL',
          description: (
            <Styled.Description>
              <Styled.DescriptionText>
                Please make sure you have all the details ready, and you have checked that your PIL:
              </Styled.DescriptionText>
              <Styled.DescriptionList>
                <Styled.DescriptionListItem>is not password protected</Styled.DescriptionListItem>
                <Styled.DescriptionListItem>is not a scanned document</Styled.DescriptionListItem>
                <Styled.DescriptionListItem>is not an artwork version of the PIL</Styled.DescriptionListItem>
                <Styled.DescriptionListItem>
                  is high quality so that users can print and read it easily
                </Styled.DescriptionListItem>
              </Styled.DescriptionList>
              <Styled.DescriptionSubtext>
                Further information on document acceptance criteria can be found{' '}
                <StyledLink to={process.env.REACT_APP_HELP_PRODUCT_GROUP_URL!} target="_blank">
                  here &gt;
                </StyledLink>
              </Styled.DescriptionSubtext>
            </Styled.Description>
          ),
          fileSubText: '.PDF 3MB max',
        };
      }

    case DocumentType.Epil:
      if (documentProcessingFlow === DocumentProcessingFlow.qrdPil) {
        // For epil request in qrd flow -> file is pil docx
        return {
          title: `${mode} ePIL`,
          fileUploadTitle: 'Upload your ePIL',
          description: (
            <Styled.Description>
              <Styled.DescriptionText>
                Please make sure you have all the details ready, and you have checked that your ePIL:
              </Styled.DescriptionText>
              <Styled.DescriptionList>
                <Styled.DescriptionListItem>
                  is the word (docx) QRD version submitted to the regulatory authority
                </Styled.DescriptionListItem>
                <Styled.DescriptionListItem>is not password protected</Styled.DescriptionListItem>
                <Styled.DescriptionListItem>is not a scanned document</Styled.DescriptionListItem>
                <Styled.DescriptionListItem>is not a different version to the submitted PIL</Styled.DescriptionListItem>
                <Styled.DescriptionListItemBold>is not in compatibility mode for versions of MS Word prior to 2013</Styled.DescriptionListItemBold>
              </Styled.DescriptionList>
              <Styled.DescriptionSubtext>
                Further information on document acceptance criteria can be found{' '}
                <StyledLink to={process.env.REACT_APP_HELP_PRODUCT_GROUP_URL!} target="_blank">
                  here &gt;
                </StyledLink>
              </Styled.DescriptionSubtext>
            </Styled.Description>
          ),
          fileSubText: '.DOCX (Word 2013 or later) 3MB max',
        };
      } else {
        // For epil request in pdf flow -> file is alternative text
        return {
          title: `Request ePIL`,
          fileUploadTitle: 'Upload your ePIL',
          description: (
            <Styled.Description>
              <Styled.DescriptionText>
                Please make sure you have all the details ready, and you have checked that your ePIL:
              </Styled.DescriptionText>
              <Styled.DescriptionList>
                <Styled.DescriptionListItem>
                  is the word (docx) QRD version submitted to the regulatory authority
                </Styled.DescriptionListItem>
                <Styled.DescriptionListItem>is not password protected</Styled.DescriptionListItem>
                <Styled.DescriptionListItem>is not a scanned document</Styled.DescriptionListItem>
                <Styled.DescriptionListItem>is not a different version to the submitted PIL</Styled.DescriptionListItem>
                <Styled.DescriptionListItemBold>is not in compatibility mode for versions of MS Word prior to 2013</Styled.DescriptionListItemBold>
              </Styled.DescriptionList>
              <Styled.DescriptionSubtext>
                Further information on document acceptance criteria can be found{' '}
                <StyledLink to={process.env.REACT_APP_HELP_PRODUCT_GROUP_URL!} target="_blank">
                  here &gt;
                </StyledLink>
              </Styled.DescriptionSubtext>
            </Styled.Description>
          ),
          fileSubText: '.DOCX (Word 2013 or later) 3MB max',
        };
      }

    case DocumentType.Rmm:
      return {
        title: `${mode} RMM`,
        fileUploadTitle: 'Upload your RMM',
        description: (
          <Styled.Description>
            <Styled.DescriptionText>
              Please make sure you have all the details ready, and you have checked that your RMM:
            </Styled.DescriptionText>
            <Styled.DescriptionList>
              <Styled.DescriptionListItem>is not password protected</Styled.DescriptionListItem>
              <Styled.DescriptionListItem>is not a scanned document</Styled.DescriptionListItem>
              <Styled.DescriptionListItem>
                has no hidden metadata or personal information saved with the document
              </Styled.DescriptionListItem>
              <Styled.DescriptionListItem>is high quality so that users can print and read it easily</Styled.DescriptionListItem>
            </Styled.DescriptionList>
            <Styled.DescriptionSubtext>
              Further information on document acceptance criteria can be found{' '}
              <StyledLink to={process.env.REACT_APP_HELP_PRODUCT_GROUP_URL!} target="_blank">
                here &gt;
              </StyledLink>
            </Styled.DescriptionSubtext>
          </Styled.Description>
        ),
        fileSubText: '.PDF 3MB max',
      };

    case DocumentType.Dhcp:
      return {
        title: `${mode} DHPC`,
        fileUploadTitle: 'Upload your DHPC',
        description: (
          <Styled.Description>
            <Styled.DescriptionText>
              Please make sure you have all the details ready, and you have checked that your DHPC:
            </Styled.DescriptionText>
            <Styled.DescriptionList>
              <Styled.DescriptionListItem>is not password protected</Styled.DescriptionListItem>
              <Styled.DescriptionListItem>is not a scanned document</Styled.DescriptionListItem>
              <Styled.DescriptionListItem>
                has no hidden metadata or personal information saved with the document
              </Styled.DescriptionListItem>
              <Styled.DescriptionListItem>is high quality so that users can print and read it easily</Styled.DescriptionListItem>
            </Styled.DescriptionList>
            <Styled.DescriptionSubtext>
              Further information on document acceptance criteria can be found{' '}
              <StyledLink to={process.env.REACT_APP_HELP_PRODUCT_GROUP_URL!} target="_blank">
                here &gt;
              </StyledLink>
            </Styled.DescriptionSubtext>
          </Styled.Description>
        ),
        fileSubText: '.PDF 3MB max',
      };

    case DocumentType.UserManual:
      return {
        title: `${mode} User Manual`,
        fileUploadTitle: 'Upload your User Manual',
        description: (
          <Styled.Description>
            <Styled.DescriptionText>
              Please make sure you have all the details ready, and you have checked that your User Manual:
            </Styled.DescriptionText>
            <Styled.DescriptionList>
              <Styled.DescriptionListItem>is not password protected</Styled.DescriptionListItem>
              <Styled.DescriptionListItem>is not a scanned document</Styled.DescriptionListItem>
              <Styled.DescriptionListItem>
                has no hidden metadata or personal information saved with the document
              </Styled.DescriptionListItem>
              <Styled.DescriptionListItem>is high quality so that users can print and read it easily</Styled.DescriptionListItem>
            </Styled.DescriptionList>
            <Styled.DescriptionSubtext>
              Further information on document acceptance criteria can be found{' '}
              <StyledLink to={process.env.REACT_APP_HELP_PRODUCT_GROUP_URL!} target="_blank">
                here &gt;
              </StyledLink>
            </Styled.DescriptionSubtext>
          </Styled.Description>
        ),
        fileSubText: '.PDF 3MB max',
      };

    case DocumentType.SafetyAlert:
      return {
        title: `${mode} Safety information`,
        fileUploadTitle: 'Upload your Safety information',
        description: (
          <Styled.Description>
            <Styled.DescriptionSubtext>
              Please enter the description of the Safety alert as you would like it to appear on emc.
            </Styled.DescriptionSubtext>
            <Styled.DescriptionList>
              <Styled.DescriptionListItem>Please ensure that your link starts with https://</Styled.DescriptionListItem>
            </Styled.DescriptionList>
          </Styled.Description>
        ),
        fileSubText: '.PDF 3MB max',
      };

    case DocumentType.ProductInformation:
      return {
        title: `${mode} Product information`,
        fileUploadTitle: 'Upload your Product information',
        description: (
          <Styled.Description>
            <Styled.DescriptionSubtext>
              Please enter the description of the Product information as you would like it to appear on emc.
            </Styled.DescriptionSubtext>
            <Styled.DescriptionList>
              {`This is a free-text box that can be used to advise HCP's and patients of non-promotional information such as stock availability or changes to product packaging (subject to review by our Data Quality Team)`}
            </Styled.DescriptionList>
          </Styled.Description>
        ),
        fileSubText: '.PDF 3MB max',
      };

    case DocumentType.AudioVideo:
      return {
        title: `${mode} Audio/Video`,
        fileUploadTitle: 'Add link to your Audio/Video',
        description: (
          <Styled.Description>
            <Styled.DescriptionText>
              Please make sure you have all the details ready, and you have checked that your Audio/Video:
            </Styled.DescriptionText>
            <Styled.DescriptionList>
              <Styled.DescriptionListItem>link is proper</Styled.DescriptionListItem>
            </Styled.DescriptionList>
            <Styled.DescriptionSubtext>
              Further information on document acceptance criteria can be found{' '}
              <StyledLink to={process.env.REACT_APP_HELP_PRODUCT_GROUP_URL!} target="_blank">
                here &gt;
              </StyledLink>
            </Styled.DescriptionSubtext>
          </Styled.Description>
        ),
      };

    case DocumentType.Audio:
      return {
        title: `${mode} Audio`,
        fileUploadTitle: 'Add link to your Audio',
        description: (
          <Styled.Description>
            <Styled.DescriptionText>
              <p>
                We will host the content so we require a link to a location where we can download the file for review and upload
                to our hosting platform.
              </p>
              <br />
              <p>Audio formats accepted:</p>
            </Styled.DescriptionText>
            <Styled.DescriptionList>
              <Styled.DescriptionListItem>WAV</Styled.DescriptionListItem>
              <Styled.DescriptionListItem>MP3</Styled.DescriptionListItem>
              <Styled.DescriptionListItem>MPEG4</Styled.DescriptionListItem>
              <Styled.DescriptionListItem>AIFF</Styled.DescriptionListItem>
            </Styled.DescriptionList>
            <Styled.DescriptionSubtext>
              Further information on content acceptance criteria can be found{' '}
              <StyledLink to={process.env.REACT_APP_HELP_PRODUCT_GROUP_URL!} target="_blank">
                here &gt;
              </StyledLink>
            </Styled.DescriptionSubtext>
          </Styled.Description>
        ),
      };

    case DocumentType.Video:
      return {
        title: `${mode} Video`,
        fileUploadTitle: 'Add link to your Video',
        description: (
          <Styled.Description>
            <Styled.DescriptionText>
              <p>
                We will host the content so we require a link to a location where we can download the file for review and upload
                to our hosting platform.
              </p>
              <br />
              <p>Video formats accepted:</p>
            </Styled.DescriptionText>
            <Styled.DescriptionList>
              <Styled.DescriptionListItem>MP4</Styled.DescriptionListItem>
              <Styled.DescriptionListItem>MOV</Styled.DescriptionListItem>
              <Styled.DescriptionListItem>WMV</Styled.DescriptionListItem>
            </Styled.DescriptionList>
            <Styled.DescriptionSubtext>
              Further information on content acceptance criteria can be found{' '}
              <StyledLink to={process.env.REACT_APP_HELP_PRODUCT_GROUP_URL!} target="_blank">
                here &gt;
              </StyledLink>
            </Styled.DescriptionSubtext>
          </Styled.Description>
        ),
      };

    default:
      return {};
  }
};
